import { ApiError, RateLimitError, UnauthenticatedError } from "./api.js";

/**
 * GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — admin QueryClient retry
 * policy. Mirror of the web policy (`apps/web/src/lib/rateLimitRetry.ts`):
 * a 429 must reduce traffic, never retry immediately.
 *
 *   - 401 → never retried (cache redirects to login).
 *   - 429 → retried at most once, only after a SHORT `Retry-After`
 *     window; a long window fails fast so the page can show a
 *     controlled message instead of hanging / retrying into a still-
 *     closed window.
 *   - other 4xx → never retried.
 *   - network / 5xx → one backoff retry (legacy behaviour).
 */

export const TRANSIENT_MAX_RETRIES = 1;
export const RATE_LIMIT_MAX_RETRIES = 1;
export const RATE_LIMIT_AUTO_RETRY_MAX_SECONDS = 15;
export const MAX_RETRY_DELAY_MS = 15_000;

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof UnauthenticatedError) return false;
  if (error instanceof RateLimitError) {
    if (failureCount >= RATE_LIMIT_MAX_RETRIES) return false;
    const wait = error.retryAfterSeconds;
    return wait == null || wait <= RATE_LIMIT_AUTO_RETRY_MAX_SECONDS;
  }
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < TRANSIENT_MAX_RETRIES;
}

export function retryDelayForQuery(
  failureCount: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  if (error instanceof RateLimitError && error.retryAfterSeconds != null) {
    const base = Math.min(error.retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS);
    return base + base * 0.2 * random();
  }
  const base = Math.min(1000 * 2 ** failureCount, MAX_RETRY_DELAY_MS);
  return base + base * 0.2 * random();
}

export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}
